import { useMemo } from 'react';
import type { PickViewMode } from './useMyTeamPickState';
import { usePlayers } from '@hooks/usePlayers';
import { useAccessToken } from '@hooks/useAccessToken';
import type { PlayerPriceChange } from '@api-service/players';

type UseMyTeamPriceChangesResult = {
  priceChangeMap: Map<string, PlayerPriceChange>;
  isLoading: boolean;
};

const PRICE_CHANGE_VIEW: PickViewMode = 'priceChanges';

/**
 * Fetches predicted price changes for the current squad, keyed by player id.
 * Only runs while the price-changes view is active. Mirrors the scoreline-app
 * `useMyTeamPriceChanges` hook. An upstream failure simply yields an empty map,
 * so the tiles degrade to a neutral state rather than breaking the lineup.
 */
export const useMyTeamPriceChanges = (
  squadPlayerIds: string[],
  viewMode: PickViewMode
): UseMyTeamPriceChangesResult => {
  const { accessToken } = useAccessToken();
  const isPriceChangeView = viewMode === PRICE_CHANGE_VIEW;
  // Normalise before joining so reordering the squad (e.g. a substitution
  // swapping starting/bench order) doesn't change the query key and trigger an
  // unnecessary refetch of an identical player set. Mirrors usePlayerEnrichment.
  const normalisedIds = [...new Set(squadPlayerIds.filter(Boolean))].sort();
  const ids = normalisedIds.join(',');

  const { data, isFetching } = usePlayers({
    accessToken: accessToken ?? undefined,
    ids: ids.length > 0 ? ids : undefined,
    include: 'priceChange',
    limit: normalisedIds.length || 1,
    enabled: isPriceChangeView && normalisedIds.length > 0
  });

  const priceChangeMap = useMemo(() => {
    const map = new Map<string, PlayerPriceChange>();
    if (!data) return map;
    for (const page of data.pages) {
      for (const player of page.data) {
        if (player.id && player.priceChange) {
          map.set(String(player.id), player.priceChange);
        }
      }
    }
    return map;
  }, [data]);

  return {
    priceChangeMap,
    isLoading: isFetching && isPriceChangeView
  };
};
